import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Textarea } from '../components/ui/textarea.jsx';
import { Label } from '../components/ui/label.jsx';
import { Badge } from '../components/ui/badge.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog.jsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table.jsx';
import { 
  Plus,
  Edit2,
  Trash2,
  Filter,
  Search,
  Download,
  AlertCircle,
  CheckCircle,
  XCircle,
  Settings,
  RefreshCw
} from 'lucide-react';
import { supabase } from '../api/supabaseClient.js';
import { User } from '../api/supabaseEntities.js';

export default function SystemModules() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [modules, setModules] = useState([]);
  const [systems, setSystems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [systemFilter, setSystemFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    version: '',
    status: 'active',
    module_type: 'feature',
    is_core: false,
    system_id: '',
    dependencies: [],
    configurations: {}
  });

  useEffect(() => {
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    console.log('🚀 Inicializando SystemModules...');
    try {
      setIsLoading(true);
      setError(null);
      
      // Verificar autenticação usando User.me()
      console.log('👤 Verificando usuário autenticado...');
      const userData = await User.me();
      
      if (!userData) {
        console.log('🚫 Usuário não autenticado');
        navigate('/AdminLogin');
        return;
      }

      console.log('✅ Usuário autenticado:', userData.email);
      console.log('👥 Role do usuário:', userData.role);
      
      setUserRole(userData.role);
      setCurrentUser(userData); // Adicionar esta linha

      // Verificar se é admin
      const isAdmin = userData.role === 'admin' || userData.is_admin;
      console.log('🔐 Is Admin:', isAdmin);
      
      if (!isAdmin) {
        console.log('🚫 Acesso negado - não é admin');
        setError('Acesso negado. Apenas administradores podem acessar esta página.');
        return;
      }

      // Carregar dados em sequência (como em Systems.jsx)
      console.log('📊 Carregando dados em sequência...');
      await loadSystems(userData);
      await loadModules(userData);
      
              console.log('✅ Componente inicializado com sucesso');
      } catch (err) {
        console.error('💥 Erro na inicialização:', err);
        setError('Erro inesperado: ' + err.message);
      } finally {
        setIsLoading(false);
      }
    };



  const loadSystems = async (userData = null) => {
    try {
      console.log('🔄 Carregando sistemas...');
      
      // Usar a mesma estratégia que funciona em Systems.jsx
      // Tentar usar RPC primeiro
      const { data: rpcResult, error: rpcError } = await supabase.rpc('get_systems_with_custom_auth', {
        input_user_id: currentUser?.id || null
      });
      
      console.log('📊 Resultado RPC systems:', { rpcResult, rpcError });
      
      if (rpcError) {
        console.warn('⚠️ RPC falhou, tentando query direta:', rpcError);
        
        // Fallback: query direta
        const { data, error } = await supabase
          .from('systems')
          .select('id, name, description, version, status, company_id, type, license_type, installation_date, expiration_date, support_level, documentation_url, repository_url, system_code, system_category, system_subcategory, system_notes, created_at, updated_at')
          .order('name');
        
        console.log('📊 Resultado query direta systems:', { data, error });
        
        if (error) {
          console.error('❌ Erro na query direta:', error);
          // Em caso de erro, usar sistemas de exemplo para não quebrar a aplicação
          const exemploSistemas = [
            {
              id: 'exemplo-1',
              name: 'Sistema Exemplo 1',
              description: 'Sistema de exemplo para teste de módulos',
              version: '1.0.0',
              status: 'active',
              type: 'example'
            },
            {
              id: 'exemplo-2', 
              name: 'Sistema Exemplo 2',
              description: 'Outro sistema de exemplo',
              version: '2.0.0',
              status: 'active',
              type: 'example'
            }
          ];
          
          console.log('📝 Usando sistemas de exemplo devido ao erro:', exemploSistemas);
          setSystems(exemploSistemas);
          return;
        }
        
        console.log(`✅ ${(data || []).length} sistemas carregados via query direta:`, data);
        setSystems(data || []);
        return;
      }
      
      if (!rpcResult.success) {
        throw new Error(rpcResult.error);
      }
      
      const systemsArray = rpcResult.data || [];
      console.log(`✅ ${systemsArray.length} sistemas carregados via RPC:`, systemsArray);
      setSystems(systemsArray);
      
    } catch (error) {
      console.error('💥 Erro ao carregar sistemas:', error);
      // Em caso de erro, definir array vazio para não quebrar a aplicação
      setSystems([]);
      console.warn('⚠️ Continuando sem sistemas carregados');
    }
  };

  const loadModules = async (userData = null) => {
    try {
      console.log('🔄 Carregando módulos via RPC... [VERSÃO V2 CORRIGIDA - SEM AMBIGUIDADE]');
      const userToUse = userData || currentUser;
      console.log('👤 userToUse no loadModules:', userToUse?.id);
      
      // USAR A RPC V2 CORRIGIDA (SEM AMBIGUIDADE)
      const { data: rpcResult, error: rpcError } = await supabase.rpc('get_modules_with_custom_auth_v2', {
        input_user_id: userToUse?.id || null
      });
      
      console.log('📊 Resultado RPC modules [V2 CORRIGIDA]:', { 
        rpcResult, 
        rpcError,
        success: rpcResult?.success,
        data: rpcResult?.data?.length || 'N/A'
      });
      
      if (rpcError) {
        throw new Error(`RPC Error: ${rpcError.message}`);
      }
      
      if (!rpcResult || !rpcResult.success) {
        throw new Error(rpcResult?.error || 'RPC returned unsuccessful result');
      }
      
      const modulesArray = rpcResult.data || [];
      console.log(`✅ ${modulesArray.length} módulos carregados via RPC:`, modulesArray);
      setModules(modulesArray);
      
    } catch (error) {
      console.error('💥 Erro ao carregar módulos via RPC, tentando fallback:', error);
      
      // Fallback: Query direta com JOIN
      try {
        console.log('🔄 Fallback: query direta com JOIN...');
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('system_modules')
          .select(`
            id, name, description, version, status, module_type, is_core, system_id, dependencies, configurations,
            systems!inner(name)
          `)
          .order('name');
        
        console.log('📊 Resultado fallback com JOIN:', { fallbackData, fallbackError, count: fallbackData?.length });
        
        if (!fallbackError && fallbackData) {
          // Ajustar estrutura para match com RPC
          const adjustedData = fallbackData.map(module => ({
            ...module,
            system_name: module.systems?.name || null
          }));
          
          console.log(`✅ ${adjustedData.length} módulos carregados via fallback:`, adjustedData);
          setModules(adjustedData);
          return;
        }
      } catch (fallbackErr) {
        console.warn('⚠️ Fallback falhou:', fallbackErr);
      }
      
      // Se tudo falhou, array vazio
      console.warn('⚠️ Todas as estratégias falharam, definindo array vazio');
      setModules([]);
    }
  };

  const filteredModules = modules.filter(module => {
    const matchesSearch = module.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         module.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || module.status === statusFilter;
    
    const matchesSystem = systemFilter === 'all' || module.system_id === systemFilter;
    
    return matchesSearch && matchesStatus && matchesSystem;
  });

  // Debug logs para entender a filtragem
  React.useEffect(() => {
    console.log('🔍 Debug filtros:', {
      totalModules: modules.length,
      searchTerm,
      statusFilter,
      systemFilter,
      filteredCount: filteredModules.length,
      sampleModules: modules.slice(0, 3).map(m => ({
        id: m.id,
        name: m.name,
        status: m.status,
        system_id: m.system_id
      }))
    });
  }, [modules, searchTerm, statusFilter, systemFilter, filteredModules.length]);

  const handleCreateModule = () => {
    setEditingModule(null);
    setFormData({
      name: '',
      description: '',
      version: '',
      status: 'active',
      module_type: 'feature',
      is_core: false,
      system_id: '',
      dependencies: [],
      configurations: {}
    });
    setIsModalOpen(true);
  };

  const handleEditModule = (module) => {
    setEditingModule(module);
    setFormData({
      name: module.name || '',
      description: module.description || '',
      version: module.version || '',
      status: module.status || 'active',
      module_type: module.module_type || 'feature',
      is_core: module.is_core || false,
      system_id: module.system_id || '',
      dependencies: module.dependencies || [],
      configurations: module.configurations || {}
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Verificar se o usuário está autenticado
      if (!currentUser) {
        throw new Error('Usuário não autenticado');
      }
      
      // Verificar se o usuário tem permissões de admin
      const isAdmin = currentUser?.role === 'admin' || currentUser?.is_admin;
      const userId = currentUser?.user_id || currentUser?.id;
      
      console.log('🔐 Verificação de permissões:', {
        userId,
        isAdmin,
        userRole: currentUser?.role,
        isAdminFlag: currentUser?.is_admin
      });

      if (!isAdmin) {
        throw new Error('Apenas administradores podem gerenciar módulos do sistema');
      }
      
      console.log('🔍 Current User:', currentUser);
      
      // Validar campos obrigatórios
      if (!formData.name || !formData.system_id) {
        throw new Error('Nome e Sistema são obrigatórios');
      }

      const payload = {
        ...formData,
        dependencies: JSON.stringify(formData.dependencies),
        configurations: JSON.stringify(formData.configurations)
      };

      console.log('📤 Dados a serem enviados:', payload);

      if (editingModule) {
        // Atualização via RPC para bypass RLS
        console.log('🔄 Atualizando módulo via RPC...');
        
        const { data: rpcResult, error: rpcError } = await supabase.rpc('update_module_with_custom_auth', {
          module_id: editingModule.id,
          module_data: payload,
          input_user_id: currentUser?.id
        });
        
        console.log('📊 Resultado RPC update:', { rpcResult, rpcError });
        
        if (rpcError) {
          console.error('❌ Erro RPC na atualização:', rpcError);
          throw rpcError;
        }
        
        if (!rpcResult?.success) {
          console.error('❌ RPC retornou erro:', rpcResult?.error);
          throw new Error(rpcResult?.error || 'Falha na atualização via RPC');
        }
        
        console.log('✅ Módulo atualizado via RPC:', rpcResult.data);
      } else {
        // Criação via RPC para bypass RLS
        console.log('🔄 Criando módulo via RPC...');
        
        const { data: rpcResult, error: rpcError } = await supabase.rpc('create_module_with_custom_auth', {
          module_data: payload,
          input_user_id: currentUser?.id
        });
        
        console.log('📊 Resultado RPC create:', { rpcResult, rpcError });
        
        if (rpcError) {
          console.error('❌ Erro RPC na criação:', rpcError);
          throw rpcError;
        }
        
        if (!rpcResult?.success) {
          console.error('❌ RPC retornou erro:', rpcResult?.error);
          throw new Error(rpcResult?.error || 'Falha na criação via RPC');
        }
        
        console.log('✅ Módulo criado via RPC:', rpcResult.data);
      }

      await loadModules();
      setIsModalOpen(false);
    } catch (err) {
      console.error('Erro ao salvar módulo:', err);
      setError('Erro ao salvar módulo: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteModule = async (moduleId) => {
    if (!window.confirm('Tem certeza que deseja excluir este módulo?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('system_modules')
        .delete()
        .eq('id', moduleId);
      
      if (error) throw error;
      
      await loadModules();
    } catch (err) {
      console.error('Erro ao excluir módulo:', err);
      setError('Erro ao excluir módulo: ' + err.message);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p>Carregando módulos do sistema...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Erro ao carregar</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={initializeComponent} variant="outline">
                <RefreshCw className="h-4 w-4 mr-2" />
                Tentar novamente
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Módulos do Sistema [ATUALIZADO]</h1>
          <p className="text-gray-600">Gerencie os módulos dos sistemas</p>
          {/* Debug temporário */}
          <div className="flex gap-2 mt-2">
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => {
                console.log('🔍 Estado atual:', { 
                  modules: modules.length, 
                  systems: systems.length,
                  filteredModules: filteredModules.length,
                  searchTerm,
                  statusFilter,
                  systemFilter
                });
              }}
            >
              Debug Estado
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => loadModules(currentUser)}
            >
              Recarregar Módulos
            </Button>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={async () => {
                console.log('🧪 TESTE RPC V2: get_modules_with_custom_auth_v2...');
                try {
                  // Teste da RPC V2 corrigida
                  const { data: rpcResult, error: rpcError } = await supabase.rpc('get_modules_with_custom_auth_v2', {
                    input_user_id: currentUser?.id || null
                  });
                  
                  console.log('📊 Resultado RPC V2:', { 
                    rpcResult, 
                    rpcError,
                    success: rpcResult?.success,
                    count: rpcResult?.data?.length || 0
                  });
                  
                  if (!rpcError && rpcResult?.success) {
                    console.log('✅ RPC V2 FUNCIONOU! Atualizando state...');
                    setModules(rpcResult.data || []);
                  } else {
                    console.log('❌ RPC V2 falhou:', rpcError?.message || rpcResult?.error);
                  }
                } catch (err) {
                  console.error('❌ Erro no teste RPC V2:', err);
                }
              }}
            >
              Teste RPC V2 ✅
            </Button>

          </div>
        </div>
        <Button onClick={handleCreateModule}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Módulo
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="search">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  placeholder="Nome ou descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <Label>Sistema</Label>
              <Select value={systemFilter} onValueChange={setSystemFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os sistemas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os sistemas</SelectItem>
                  {systems.map((system) => (
                    <SelectItem key={system.id} value={system.id}>
                      {system.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="inactive">Inativo</SelectItem>
                  <SelectItem value="deprecated">Depreciado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end">
              <Button variant="outline" onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setSystemFilter('all');
              }}>
                <Filter className="h-4 w-4 mr-2" />
                Limpar Filtros
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Módulos */}
      <Card>
        <CardHeader>
          <CardTitle>
            Módulos ({filteredModules.length} de {modules.length} total)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredModules.length === 0 ? (
            <div className="text-center py-8">
              <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Nenhum módulo encontrado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Sistema</TableHead>
                  <TableHead>Versão</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Core</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredModules.map((module) => (
                  <TableRow key={module.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{module.name}</div>
                        {module.description && (
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {module.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {module.systems?.name || 'N/A'}
                    </TableCell>
                    <TableCell>{module.version || 'N/A'}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {module.module_type || 'feature'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={
                          module.status === 'active' ? 'default' : 
                          module.status === 'inactive' ? 'secondary' : 'outline'
                        }
                        className={
                          module.status === 'active' ? 'bg-green-100 text-green-800 border-green-300' :
                          module.status === 'inactive' ? 'bg-gray-100 text-gray-800 border-gray-300' :
                          'bg-red-100 text-red-800 border-red-300'
                        }
                      >
                        {module.status === 'active' && <CheckCircle className="w-3 h-3 mr-1" />}
                        {module.status === 'inactive' && <XCircle className="w-3 h-3 mr-1" />}
                        {module.status === 'deprecated' && <AlertCircle className="w-3 h-3 mr-1" />}
                        {module.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {module.is_core ? (
                        <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                          Core
                        </Badge>
                      ) : (
                        <Badge variant="outline">
                          Opcional
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditModule(module)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteModule(module.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal de Criar/Editar */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {editingModule ? 'Editar Módulo' : 'Novo Módulo'}
            </DialogTitle>
            <DialogDescription>
              {editingModule 
                ? 'Edite as informações do módulo do sistema.' 
                : 'Preencha as informações para criar um novo módulo do sistema.'
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="version">Versão</Label>
                <Input
                  id="version"
                  value={formData.version}
                  onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sistema *</Label>
                <Select 
                  value={formData.system_id} 
                  onValueChange={(value) => setFormData({ ...formData, system_id: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um sistema" />
                  </SelectTrigger>
                  <SelectContent>
                    {systems.map((system) => (
                      <SelectItem key={system.id} value={system.id}>
                        {system.name}{system.version ? ` v${system.version}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Tipo</Label>
                <Select 
                  value={formData.module_type} 
                  onValueChange={(value) => setFormData({ ...formData, module_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feature">Feature</SelectItem>
                    <SelectItem value="plugin">Plugin</SelectItem>
                    <SelectItem value="extension">Extension</SelectItem>
                    <SelectItem value="integration">Integration</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                    <SelectItem value="deprecated">Depreciado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_core"
                  checked={formData.is_core}
                  onChange={(e) => setFormData({ ...formData, is_core: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="is_core">Módulo Core</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Salvando...' : (editingModule ? 'Atualizar' : 'Criar')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}